import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const res = await api.get('/auth/me');
    setUser(res.data);
    sessionStorage.setItem('user', JSON.stringify(res.data));
    return res.data;
  };

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    sessionStorage.setItem('token', res.data.token);
    sessionStorage.setItem('user', JSON.stringify(res.data.user));
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(res.data.user);
    window.dispatchEvent(new CustomEvent('user-login'));
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post('/auth/register', payload);
    sessionStorage.setItem('token', res.data.token);
    sessionStorage.setItem('user', JSON.stringify(res.data.user));
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(res.data.user);
    window.dispatchEvent(new CustomEvent('user-login'));
    return res.data.user;
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.dispatchEvent(new CustomEvent('user-logout'));
    window.location.href = '/login';
  };

  useEffect(() => {
    const init = async () => {
      const storedToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      // Migrate older localStorage auth state to per-tab session storage.
      if (localStorage.getItem('token') && !sessionStorage.getItem('token')) {
        sessionStorage.setItem('token', localStorage.getItem('token'));
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          sessionStorage.setItem('user', storedUser);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }

      try {
        await refreshUser();
      } catch {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      login,
      register,
      logout,
      refreshUser,
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
